import React, { useRef, useEffect } from "react";
import { injectIntl, intlShape } from "@ai360/core/intl";
import PropTypes from "prop-types";

import { ReportAPI } from "@ai360/core";

import { Checkbox, DateInput, Section, SubSection } from "~/core";
import CroppingSeason from "~/reports/components/inputs/cropping-season";
import { messages } from "~/reports/i18n-messages";
import { model } from "~/reports/data";
import { getReportDataByReportType } from "~/reports/data/selectors";
import { getSelectedCustomerGuids } from "~/customer-data/selectors";
import { useAppSelector } from "~/store/hooks";

import { ReportOptionsComponent, ReportOptionsComponentProps } from ".";
import { getSelectedEventGuidSet } from "~/action-panel/components/event-module/components/event-list/selectors";
import { getSelectedRecGuidSet } from "~/action-panel/components/rec-module/components/rec-list/selectors";
import { getEventGeneralGuidToEventSummaryMap } from "~/recs-events/events/selectors";
import { selectors as picklistSelectors, picklistNames } from "~/core/picklist";

import { keywords } from "../keywords";
import { getRecGeneralGuidToRecSummaryMap } from "~/recs-events/recs/selectors";

function NutrientBudget({ intl: { formatMessage }, onInputChange }: ReportOptionsComponentProps) {
    const reportData = useAppSelector(getReportDataByReportType(NutrientBudget.reportName));
    const selectedCustomerGuids = useAppSelector(getSelectedCustomerGuids);

    useEffect(() => {
        onInputChange(model.PROPS_CUSTOMER_GUIDS, Array.from(selectedCustomerGuids));
    }, [selectedCustomerGuids.length]);

    const seasons = useAppSelector((state) =>
        picklistSelectors.getPicklistOptionsFromCode(
            state,
            picklistNames.getPickListCode(picklistNames.PICKLIST_CROPPING_SEASON)
        )
    );

    const validRecTypes = [keywords.applicationManual, keywords.applicationEquation];
    const agEventSummaryMap = useAppSelector(getEventGeneralGuidToEventSummaryMap);
    const selectedEventGuidList = useAppSelector(getSelectedEventGuidSet);
    const selectedRecGuidList = useAppSelector(getSelectedRecGuidSet);
    const agRecSummaryMap = useAppSelector(getRecGeneralGuidToRecSummaryMap);
    const seasonsToDisplay = useRef<string[]>();
    const disableOption = useRef<boolean>(false);

    useEffect(() => {
        onInputChange(model.PROPS_SELECTED_EVENT_GUID_LIST, [...selectedEventGuidList]);
        onInputChange(model.PROPS_SELECTED_REC_GUID_LIST, [...selectedRecGuidList]);

        if (selectedEventGuidList.size > 0 || selectedRecGuidList.size > 0) {
            const seasonGuids = [];

            for (const eventGeneralGuid of selectedEventGuidList) {
                const event = agEventSummaryMap.get(eventGeneralGuid);
                if (event.agEventTypeName === keywords.applicationEvent) {
                    seasonGuids.push(event.croppingSeasonGuid);
                }
            }

            for (const recGeneralGuid of selectedRecGuidList) {
                const rec = agRecSummaryMap.get(recGeneralGuid);
                if (validRecTypes.includes(rec.recType)) {
                    seasonGuids.push(rec.croppingSeasonGuid);
                }
            }

            const seasonsText = [];
            for (const seasonGuid of seasonGuids) {
                const selectedSeason = seasons.find((season) => season.value === seasonGuid);
                if (seasonsText.indexOf(selectedSeason.label) === -1) {
                    seasonsText.push(selectedSeason.label);
                }
            }

            seasonsText.sort();
            seasonsToDisplay.current = seasonsText;
            let currentSelectionState = false;
            if (selectedEventGuidList.size > 0 && selectedRecGuidList.size > 0) {
                currentSelectionState =
                    disableReportOptionsOnSelectedEvent(selectedEventGuidList) ||
                    disableReportOptionsOnSelectedRec(selectedRecGuidList);
            } else if (selectedEventGuidList.size > 0) {
                currentSelectionState = disableReportOptionsOnSelectedEvent(selectedEventGuidList);
            } else {
                currentSelectionState = disableReportOptionsOnSelectedRec(selectedRecGuidList);
            }
            disableOption.current = currentSelectionState;
        } else {
            seasonsToDisplay.current = [];
            disableOption.current = false;
        }
    }, [selectedEventGuidList.size, selectedRecGuidList.size]);

    const disableReportOptionsOnSelectedEvent = (selectedEventGuidList: any): boolean => {
        for (const eventGeneralGuid of selectedEventGuidList) {
            const event = agEventSummaryMap.get(eventGeneralGuid);
            if (event.agEventTypeName === keywords.applicationEvent) {
                return true;
            }
        }
    };

    const disableReportOptionsOnSelectedRec = (selectedRecGuidList: any): boolean => {
        for (const recGeneralGuid of selectedRecGuidList) {
            const rec = agRecSummaryMap.get(recGeneralGuid);
            if (validRecTypes.includes(rec.recType)) {
                return true;
            }
        }
    };

    const croppingSeasonDisabled =
        reportData[model.PROPS_START_DATE] ||
        reportData[model.PROPS_END_DATE] ||
        disableOption.current;

    const showRowLevelCostDisabled = !reportData[model.PROPS_SHOW_NUTRIENT_BUDGET_COST_COLUMN];

    return (
        <div className="report-options">
            <Section>
                <SubSection>
                    <CroppingSeason
                        disabled={croppingSeasonDisabled}
                        required={!croppingSeasonDisabled}
                        reportType={NutrientBudget.reportName}
                        valuesToDisplay={seasonsToDisplay.current}
                    />
                </SubSection>
                <SubSection>
                    <DateInput
                        placeholderText={formatMessage(messages.startDate)}
                        onChange={(value) => onInputChange(model.PROPS_START_DATE, value)}
                        timeFormat={false}
                        value={reportData[model.PROPS_START_DATE]}
                        disabled={disableOption.current}
                    />
                    <DateInput
                        placeholderText={formatMessage(messages.endDate)}
                        onChange={(value) => onInputChange(model.PROPS_END_DATE, value)}
                        timeFormat={false}
                        value={reportData[model.PROPS_END_DATE]}
                        disabled={disableOption.current}
                    />
                </SubSection>
                <SubSection>
                    <Checkbox
                        className="report-option-checkbox"
                        disabled={false}
                        label={formatMessage(messages.productDetail)}
                        onChange={(e, value) =>
                            onInputChange(model.PROPS_SHOW_NUTRIENT_BUDGET_PRODUCT_DETAIL, value)
                        }
                        value={reportData[model.PROPS_SHOW_NUTRIENT_BUDGET_PRODUCT_DETAIL]}
                    />
                    <Checkbox
                        disabled={false}
                        label={formatMessage(messages.costDetail)}
                        onChange={(e, value) =>
                            onInputChange(model.PROPS_SHOW_NUTRIENT_BUDGET_COST_COLUMN, value)
                        }
                        value={reportData[model.PROPS_SHOW_NUTRIENT_BUDGET_COST_COLUMN]}
                    />
                </SubSection>
                <SubSection>
                    <Checkbox
                        disabled={false}
                        label={formatMessage(messages.rowLevelNutrients)}
                        onChange={(e, value) =>
                            onInputChange(
                                model.PROPS_SHOW_NUTRIENT_BUDGET_ROW_LEVEL_NUTRIENTS,
                                value
                            )
                        }
                        value={reportData[model.PROPS_SHOW_NUTRIENT_BUDGET_ROW_LEVEL_NUTRIENTS]}
                    />
                    <Checkbox
                        disabled={showRowLevelCostDisabled}
                        label={formatMessage(messages.rowLevelCost)}
                        onChange={(e, value) =>
                            onInputChange(model.PROPS_SHOW_NUTRIENT_BUDGET_ROW_LEVEL_COST, value)
                        }
                        value={reportData[model.PROPS_SHOW_NUTRIENT_BUDGET_ROW_LEVEL_COST]}
                    />
                </SubSection>
                <SubSection>
                    <Checkbox
                        disabled={false}
                        label={formatMessage(messages.useEventRecName)}
                        onChange={(e, value) =>
                            onInputChange(
                                model.PROPS_SHOW_NUTRIENT_BUDGET_USE_EVENT_REC_NAME,
                                value
                            )
                        }
                        value={reportData[model.PROPS_SHOW_NUTRIENT_BUDGET_USE_EVENT_REC_NAME]}
                    />
                </SubSection>
            </Section>
        </div>
    );
}

NutrientBudget.propTypes = {
    intl: intlShape,
    reportPreferences: PropTypes.object,
};

NutrientBudget.defaultReportOptions = {
    [model.PROPS_END_DATE]: null,
    [model.PROPS_START_DATE]: null,
    [model.PROPS_SELECTED_EVENT_GUID_LIST]: [],
    [model.PROPS_SELECTED_REC_GUID_LIST]: [],
    [model.PROPS_CROPPING_SEASON_GUID_LIST]: [],
    [model.PROPS_SHOW_NUTRIENT_BUDGET_PRODUCT_DETAIL]: false,
    [model.PROPS_SHOW_NUTRIENT_BUDGET_COST_COLUMN]: false,
    [model.PROPS_SHOW_NUTRIENT_BUDGET_ROW_LEVEL_NUTRIENTS]: true,
    [model.PROPS_SHOW_NUTRIENT_BUDGET_ROW_LEVEL_COST]: true,
    [model.PROPS_SHOW_NUTRIENT_BUDGET_USE_EVENT_REC_NAME]: false,
};

NutrientBudget.reportPreferences = [
    model.PROPS_SHOW_NUTRIENT_BUDGET_PRODUCT_DETAIL,
    model.PROPS_SHOW_NUTRIENT_BUDGET_COST_COLUMN,
    model.PROPS_SHOW_NUTRIENT_BUDGET_ROW_LEVEL_NUTRIENTS,
    model.PROPS_SHOW_NUTRIENT_BUDGET_ROW_LEVEL_COST,
    model.PROPS_SHOW_NUTRIENT_BUDGET_USE_EVENT_REC_NAME,
];

NutrientBudget.reportName = ReportAPI.ReportNames.NUTRIENT_BUDGET;

export default injectIntl(NutrientBudget) as ReportOptionsComponent<ReportOptionsComponentProps>;
